import { useQuery } from '@tanstack/react-query'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { PageFrame } from 'shared/components/page-frame'
import { CompactTable, EmptyTableBody, SectionTitle } from 'shared/components/section'
import { Skeleton } from 'shared/components/ui/skeleton'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { elsewhereWords, whereNowWords } from './lookup-words'
import { formatDayTime } from 'shared/lib/utils/date'
import { Fact, Facts } from 'shared/components/facts'
import { BackButton } from './parcel-parts'

/**
 * A Container's card (ticket 48, user story 126): our Bag or a partner's Box found by
 * its code — its kind, code, partner, seal, status (open / sealed / opened), where it
 * is now and where it is bound, what it holds now and, once opened, what it held at
 * its seal, each Parcel with its stage and place. To a Point operator a Container
 * neither bound for their Point nor held there is only where it is; inside, another
 * Point's Parcel only where it is. The server decides; this renders.
 */

type ContainerAnswer = RouterOutputs['parcels']['Container']
type FullContainer = Extract<ContainerAnswer, { view: 'full' }>
type ParcelRow = FullContainer['inside'][number]

function on(at: Date | string | null): string {
	return formatDayTime(at)
}

export function ContainerCard({
	containerId,
	onBack,
	onOpenParcel,
}: {
	containerId: string
	onBack: () => void
	onOpenParcel: (parcelId: string) => void
}) {
	const { t } = useTranslate()
	const query = useQuery({ ...trpc.parcels.Container.queryOptions({ containerId }), retry: false })
	const card = query.data
	const back = <BackButton onBack={onBack} />

	if (card?.view === 'elsewhere') {
		return (
			<PageFrame
				back={back}
				heading={
					<>
						<span>
							{t(`containerKind.${card.kind}`)} {card.code}
						</span>
						<Badge variant='outline'>{t('containerCard.elsewhereTitle')}</Badge>
					</>
				}
			>
				<div data-testid='container-elsewhere'>
					<Facts>
						<Fact label={t('where.title')}>
							<span className='text-base font-medium' data-testid='container-elsewhere-where'>
								{elsewhereWords(t, card)}
							</span>
						</Fact>
					</Facts>
				</div>
			</PageFrame>
		)
	}

	if (card?.view !== 'full') {
		return (
			<PageFrame back={back} title='containerCard.title'>
				{query.isLoading && <Skeleton className='h-24 w-full' />}
				{query.isError && <p className='text-sm text-destructive'>{t('containerCard.notFound')}</p>}
			</PageFrame>
		)
	}

	return (
		<PageFrame
			back={back}
			heading={
				<>
					<span data-testid='container-code'>
						{t(`containerKind.${card.kind}`)} {card.code}
					</span>
					<Badge variant='secondary' data-testid='container-status'>
						{t(`containerStatus.${card.status}`)}
					</Badge>
				</>
			}
		>
			<Facts testId='container-card'>
				<Fact label={t('where.title')}>
					<span data-testid='container-where-now'>{card.whereNow ? whereNowWords(t, card.whereNow) : '—'}</span>
				</Fact>
				<Fact label={t('containerCard.destination')}>{card.destinationPoint?.id ?? '—'}</Fact>
				{card.partner && <Fact label={t('containerCard.partner')}>{card.partner.name}</Fact>}
				{/* A partner's Box's Hub weight; a Bag is never weighed (ADR-0047). */}
				{card.kind === 'partner_box' && (
					<Fact label={t('containerCard.weight')}>
						{card.weightKg === null
							? t('containerCard.notWeighed')
							: t('containerCard.weightValue', { weight: String(card.weightKg) })}
					</Fact>
				)}
				<Fact label={t('containerCard.sealedAt')}>{on(card.sealedAt)}</Fact>
				{card.openedAt && <Fact label={t('containerCard.openedAt')}>{on(card.openedAt)}</Fact>}
			</Facts>
			<ParcelRows
				title={t('containerCard.inside')}
				empty={t('containerCard.insideEmpty')}
				rows={card.inside}
				testId='container-inside'
				onOpen={onOpenParcel}
			/>
			{card.status === 'opened' && (
				<ParcelRows
					title={t('containerCard.heldAtSeal')}
					empty={t('containerCard.insideEmpty')}
					rows={card.heldAtSeal}
					testId='container-held-at-seal'
					onOpen={onOpenParcel}
				/>
			)}
		</PageFrame>
	)
}

/** The Parcels of a Container: one row each, a row opens its card. */
function ParcelRows({
	title,
	empty,
	rows,
	testId,
	onOpen,
}: {
	title: string
	empty: string
	rows: ParcelRow[]
	testId: string
	onOpen: (parcelId: string) => void
}) {
	const { t } = useTranslate()
	return (
		<section className='flex flex-col gap-2' data-testid={testId}>
			<SectionTitle title={title} count={rows.length} />
			<CompactTable>
				<TableHeader>
					<TableRow>
						<TableHead>{t('parcels.colCode')}</TableHead>
						<TableHead>{t('parcels.colState')}</TableHead>
						<TableHead>{t('parcels.colPoint')}</TableHead>
						<TableHead>{t('parcels.colWhere')}</TableHead>
						<TableHead />
					</TableRow>
				</TableHeader>
				{rows.length === 0 ? (
					<EmptyTableBody columns={5}>{empty}</EmptyTableBody>
				) : (
					<TableBody>
						{rows.map((row) => (
							<TableRow
								key={row.parcelId}
								className='cursor-pointer'
								data-testid='container-parcel'
								onClick={() => onOpen(row.parcelId)}
							>
								<TableCell className='font-mono'>{row.trackingCode}</TableCell>
								{row.view === 'full' ? (
									<>
										<TableCell>
											<Badge variant='secondary'>{t(`parcelState.${row.state}` as never)}</Badge>
										</TableCell>
										<TableCell>{row.destinationPoint.id}</TableCell>
										<TableCell className='whitespace-normal text-muted-foreground'>
											{row.whereNow ? whereNowWords(t, row.whereNow) : '—'}
										</TableCell>
									</>
								) : (
									<TableCell colSpan={3} className='text-muted-foreground' data-testid='container-parcel-elsewhere'>
										{elsewhereWords(t, row)}
									</TableCell>
								)}
								<TableCell className='w-px text-right'>
									<Button
										variant='ghost'
										size='sm'
										onClick={(event) => {
											event.stopPropagation()
											onOpen(row.parcelId)
										}}
									>
										{t('parcels.cardOpen')}
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</CompactTable>
		</section>
	)
}
